import React from "react";
import { Link } from "react-router-dom";

import { useWhatsAppContact } from "@/hooks/useWhatsAppContact";
import ContactFormDialog from "@/components/ContactFormDialog";
import SocialMediaLinks from "@/components/footer/SocialMediaLinks";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import AfterIdle from "@/components/util/AfterIdle";
import {
  Phone,
  Share
} from "lucide-react";

const Footer = () => {
  const { t } = useTranslation('common');
  const { whatsappNumber, isLoading } = useWhatsAppContact();

  // Create WhatsApp link
  const getWhatsAppLink = (number: string) => {
    const text = encodeURIComponent(t('footer.whatsappInquiryMessage'));
    return `https://wa.me/${number}?text=${text}`;
  };

  const getShareLink = () => {
    const text = encodeURIComponent("Check out WhatsGood HK - your guide to trusted local services in Hong Kong:");
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://whatsgoodhk.com';
    const url = encodeURIComponent(origin);
    return `https://wa.me/?text=${text}%20${url}`;
  };

  return (
  <AfterIdle timeoutMs={1200}>
    <footer className="bg-brand-charcoal text-white py-10" data-test-responsive="footer">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
          <div>
            <h3 className="text-base font-heading font-semibold mb-4">{t('footer.about')}</h3>
            <p className="text-dark-muted mb-4 text-sm leading-relaxed">
              {t('footer.aboutDescription')}
            </p>
            
            <SocialMediaLinks />
          </div>
          
          <div>
            <h3 className="text-base font-heading font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <div className="grid grid-cols-2 gap-x-4">
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.home')}
                  </Link>
                </li>
                <li>
                  <Link to="/explore" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.browseServices')}
                  </Link>
                </li>
                <li>
                  <Link to="/submit-listing" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.listYourService')}
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.aboutUs')}
                  </Link>
                </li>
              </ul>
              <ul className="space-y-2">
                <li>
                  <Link to="/pricing" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.pricing')}
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.privacyPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-dark-muted hover:text-white transition-colors text-sm">
                    {t('footer.termsOfService')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="order-3 lg:order-none">
            <h3 className="text-base font-heading font-semibold mb-4">{t('footer.getInTouch')}</h3>
            <p className="text-dark-muted mb-4 text-sm">
              {t('footer.getInTouchDescription')}
            </p>
            <div className="flex flex-col gap-3">
              <ContactFormDialog
                trigger={
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full justify-center bg-transparent border-dark-border text-white hover:bg-dark-elevated hover:border-dark-muted hover:text-white"
                  >
                    {t('footer.contactUs')}
                  </Button>
                }
              />
              
              {isLoading ? (
                <div className="text-dark-muted text-sm">{t('footer.loadingContact')}</div>
              ) : (
                <Button
                  asChild
                  variant="whatsapp"
                  size="sm"
                  className="w-full justify-center bg-whatsapp hover:bg-whatsapp-hover text-white"
                >
                  <a
                    href={getWhatsAppLink(whatsappNumber)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Contact us on WhatsApp"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {t('footer.whatsappUs')}
                  </a>
                </Button>
              )}
               <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="w-full justify-center bg-transparent border-dark-border text-white hover:bg-dark-elevated hover:border-dark-muted hover:text-white"
                >
                  <a
                    href={getShareLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Share on WhatsApp"
                  >
                    <Share className="h-4 w-4 mr-2" />
                    {t('footer.shareViaWhatsapp')}
                  </a>
                </Button>
            </div>
          </div>
        </div>
        
  <div className="border-t border-dark-border mt-10 pt-6 text-center text-dark-muted text-sm">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  </AfterIdle>
  );
};

export default Footer;
